import React from 'react'
import PropTypes from 'prop-types'
import { kebabCase } from 'lodash'
import { Link } from 'gatsby'
import Content from './Content'
import logo from '../img/logo.svg'
import xIcon from '../img/social/x.svg'

const BlogPostTemplate = ({
  content,
  contentComponent,
  description,
  tags,
  title,
  helmet,
  date,
  author,
  authorImage,
  readingTime,
  category,
  isPreview = false,
}) => {
  const PostContent = contentComponent || Content
  
  // Share functionality
  const handleShareX = () => {
    const shareText = `${title} - ${description || ''}`
    const shareUrl = typeof window !== 'undefined' ? window.location.href : ''
    const xUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    window.open(xUrl, '_blank', 'noopener,noreferrer')
  }
  
  const handleShareLinkedIn = () => {
    const shareUrl = typeof window !== 'undefined' ? window.location.href : ''
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer')
  }
  
  const handleShareFacebook = () => {
    const shareUrl = typeof window !== 'undefined' ? window.location.href : ''
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    window.open(facebookUrl, '_blank', 'noopener,noreferrer')
  }
  
  const handleCopyLink = async () => {
    if (typeof window !== 'undefined') {
      try {
        await navigator.clipboard.writeText(window.location.href)
        // Successfully copied - could add toast notification here in future
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = window.location.href
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        // Successfully copied using fallback method
      }
    }
  }
  
  return (
    <div>
      {helmet || ''}
      
      {/* Clean Article Header - Medium-inspired */}
      <section className="section pt-6-desktop pt-5-tablet pt-4-mobile pb-0">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-11-mobile is-10-tablet is-8-desktop is-7-widescreen">
              
              {/* Article Meta */}
              <div className="article-meta mb-5">
                <nav className="breadcrumb is-small has-bullet-separator" aria-label="breadcrumbs">
                  <ul>
                    <li>
                      <Link to="/blog" className="blog-breadcrumb-link">Publications</Link>
                    </li>
                    <li className="is-active">
                      <span aria-current="page" className="blog-breadcrumb-link">
                        {category || 'Article'}
                      </span>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Article Title */}
              <div className="article-header mb-6">
                <h1 className="title is-1-desktop is-2-tablet is-3-mobile has-text-weight-bold article-title">
                  {title}
                </h1>
                {description && (
                  <h2 className="subtitle is-4-desktop is-5-tablet is-6-mobile article-subtitle mt-4">
                    {description}
                  </h2>
                )}
                
                {/* Article Meta Info */}
                <div className="article-meta-info mt-5 pt-4">
                  <div className="level is-mobile">
                    <div className="level-left">
                      <div className="level-item">
                        <div className="media">
                          <div className="media-left">
                            <figure className="image is-32x32">
                              <img 
                                className="is-rounded" 
                                src={authorImage || logo} 
                                alt={author || "Green Research Team"}
                              />
                            </figure>
                          </div>
                          <div className="media-content">
                            <p className="is-size-7 has-text-weight-semibold blog-author-name">{author || "Green Research Team"}</p>
                            <p className="is-size-7 blog-meta-text">Environmental Research</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="level-right">
                      <div className="level-item">
                        <div className="has-text-right">
                          {date && (
                            <p className="is-size-7 blog-meta-text">
                              <time>{new Date(date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}</time>
                            </p>
                          )}
                          <p className="is-size-7 blog-meta-light">
                            <span className="icon-text">
                              <span className="icon is-small">
                                <i className="fas fa-clock"></i>
                              </span>
                              <span>{readingTime || '5 min'} read</span>
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section py-6-desktop py-5-tablet py-4-mobile">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-11-mobile is-10-tablet is-8-desktop is-7-widescreen">
              <div className="content article-content">
                <PostContent content={content} />
                
                {/* Tags Section */}
                {tags && tags.length ? (
                  <div className="mt-6 pt-5 article-tags">
                    <h4 className="title is-6 mb-3 blog-meta-text">Tagged</h4>
                    <div className="tags">
                      {tags.map((tag) => (
                        <Link
                          key={tag + `tag`}
                          to={`/tags/${kebabCase(tag)}/`}
                          className="tag is-medium mr-2 mb-2 hover-lift"
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Article Footer - Hide in preview mode */}
                {!isPreview && (
                  <div className="article-footer mt-6 pt-5">
                    <div className="level">
                      <div className="level-left">
                        <div className="level-item">
                          <Link to="/blog" className="button is-outlined is-primary">
                            <span className="icon">
                              <i className="fas fa-arrow-left"></i>
                            </span>
                            <span>All Publications</span>
                          </Link>
                        </div>
                      </div>
                      <div className="level-right">
                        <div className="level-item">
                          <div className="buttons">
                            <button
                              className="button is-light hover-lift share-btn-facebook"
                              title="Share on Facebook"
                              onClick={handleShareFacebook}
                            >
                              <span className="icon">
                                <i className="fab fa-facebook-f"></i>
                              </span>
                            </button>
                            <button
                              className="button is-light hover-lift share-btn-x"
                              title="Share on X"
                              onClick={handleShareX}
                            >
                              <span className="icon">
                                <img
                                  src={xIcon}
                                  alt="X"
                                  className="share-icon-svg"
                                />
                              </span>
                            </button>
                            <button
                              className="button is-light hover-lift share-btn-linkedin"
                              title="Share on LinkedIn"
                              onClick={handleShareLinkedIn}
                            >
                              <span className="icon">
                                <i className="fab fa-linkedin"></i>
                              </span>
                            </button>
                            <button
                              className="button is-light hover-lift share-btn-copy"
                              title="Copy link to clipboard"
                              onClick={handleCopyLink}
                            >
                              <span className="icon">
                                <i className="fas fa-link"></i>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
  tags: PropTypes.array,
  date: PropTypes.string,
  author: PropTypes.string,
  authorImage: PropTypes.string,
  readingTime: PropTypes.string,
  category: PropTypes.string,
  isPreview: PropTypes.bool,
}

export default BlogPostTemplate
